import React from "react";

import { DateTime } from "luxon";

import { Button, SxProps } from "@mui/material";

import usePaymentMethod from "@app/analytics/account/Billing/hooks/usePaymentMethod";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import * as Events from "@app/analytics/tracking/events";
import Link from "@app/common/components/Link";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import useLocalStorage from "@app/common/hooks/useLocalStorage";
import ROUTES from "@app/common/routes";

import Alert from "@rui/Alert";
import { Bold } from "@rui/styles";

const FREE_TRIAL_ALERT_DAY_TIMEOUT = 2;

const FreeTrialAlert = ({ sx }: { sx?: SxProps }) => {
  const { currentPlan, isTrial, subscription, plansInitialLoading } =
    useBillingContext();
  const { canEditBillingPlan } = useFeatureAccess();
  const { hasPaymentMethod } = usePaymentMethod();

  const [freeTrialAlertLastShown, setFreeTrialAlertLastShown] = useLocalStorage(
    "freeTrialAlertLastShown",
    DateTime.now()
      .minus({ days: FREE_TRIAL_ALERT_DAY_TIMEOUT + 10 })
      .toISO() // Minus X days to show the alert initially
  );

  const showFreeTrialAlert =
    !plansInitialLoading &&
    subscription.inTrialPeriod &&
    (!freeTrialAlertLastShown ||
      DateTime.now()
        .diff(DateTime.fromISO(freeTrialAlertLastShown), "days")
        .get("days") >= FREE_TRIAL_ALERT_DAY_TIMEOUT);

  // Bail early if we've already shown the alert
  if (!showFreeTrialAlert) return null;

  const handleDismissFreeTrialAlert = () => {
    // Set a 1s timeout to still show the animation
    setTimeout(() => setFreeTrialAlertLastShown(DateTime.now().toISO()), 1000);
  };

  const handleClickAddPaymentMethod = () => {
    Events.addPaymentMethod();
    setFreeTrialAlertLastShown(DateTime.now().toISO());
  };

  const showAddPaymentMethod = canEditBillingPlan && !hasPaymentMethod;
  const paymentMethodRoute = isTrial
    ? ROUTES.BILLING_COMPARE_PLANS
    : ROUTES.BILLING_UPDATE;

  return (
    <Alert
      severity="info"
      dismissible
      onDismiss={handleDismissFreeTrialAlert}
      sx={sx}
      action={
        showAddPaymentMethod && (
          <Link
            to={paymentMethodRoute}
            onClick={handleClickAddPaymentMethod}
            openInBrowser
          >
            <Button
              variant="contained"
              color="info"
              sx={{
                width: "180px",
                height: "40px",
                my: "auto",
                boxShadow: 0,
                "&:hover": { boxShadow: 0 },
              }}
            >
              Add Payment Method
            </Button>
          </Link>
        )
      }
    >
      <Bold>Free trial activated!</Bold> You are currently on a free trial of
      the {currentPlan.label} Plan.{" "}
      {showAddPaymentMethod
        ? `Add a payment method to avoid losing access to ${currentPlan.label} features once your trial ends`
        : ""}
    </Alert>
  );
};

export default FreeTrialAlert;
